// Family I / R — copilot chrome. Defence variant blocks remote copilot services (policy P-7).
//
// WP-9 cross-reference: the AI candidate review surface lives in
// `@/components/ai/{AIReviewToolCard,AiCandidateList,AiCandidateRow,AiCandidateActions,AiProvenanceFooter}`
// and shares the `AiReviewClient` interface in `@/components/ai/aiClient`.
// The dock here is the global copilot rail (chat-style) and the
// `AIReviewToolCard` is the layer-context tool card; they are different
// surfaces. When a future copilot session enters "review" mode, render
// `<AiCandidateList />` from `@/components/ai/AiCandidateList` instead of
// re-implementing the candidate row UI inline.
//
// Reference: `docs/results/VIEWER-TOOLBAR-AND-GTWIN-FIRST-UX-MASTERPLAN.md`
// §8.9 ("the dock and the card are different surfaces; the dock keeps
// wrapping the same subcomponents").
import { FormEvent, useEffect, useState } from 'react';
import { getAccessToken, getCurrentUser, getTenantContext } from '@geosoft/platform-sdk';
import { Button, Callout, Card, Field, Icon, Spinner, Stack, Text, tokens, useDefenceVariant } from '@/design';

type CopilotTool = {
  id: string;
  version: string;
  description: string;
  permissions: string[];
  defenceVariantSafe: boolean;
  costClass: string;
  reversible: boolean;
};

type CopilotTrace = {
  guardrail: string;
  decision: string;
  toolId?: string | null;
  reason?: string | null;
};

type CopilotResponse = {
  sessionId: string;
  assistantMessage: string;
  decision: string;
  toolId?: string | null;
  toolTrace: CopilotTrace[];
  auditLogged: boolean;
};

export function CopilotDock() {
  const baseUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';
  const defence = useDefenceVariant();
  const [tools, setTools] = useState<CopilotTool[]>([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<CopilotResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (defence.active) return;
    const loadTools = async () => {
      try {
        const token = await getAccessToken();
        const headers: Record<string, string> = { accept: 'application/json' };
        if (token) headers.Authorization = `Bearer ${token}`;
        const res = await fetch(`${baseUrl}/api/v1/copilot/tools`, { headers });
        if (!res.ok) throw new Error(`tools HTTP ${res.status}`);
        const data = (await res.json()) as CopilotTool[];
        setTools(data);
      } catch (err) {
        setError(`Copilot tools unavailable: ${(err as Error).message}`);
      }
    };
    void loadTools();
  }, [baseUrl, defence.active]);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (defence.active) return;
    const trimmed = message.trim();
    if (!trimmed) return;
    setLoading(true);
    setError(null);
    try {
      const tenant = getTenantContext().tenantId;
      const token = await getAccessToken();
      const headers: Record<string, string> = {
        accept: 'application/json',
        'content-type': 'application/json',
        'x-tenant-id': tenant,
      };
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(`${baseUrl}/api/v1/copilot/messages`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          sessionId: `ui-${Date.now()}`,
          userMessage: trimmed,
        }),
      });
      if (!res.ok) throw new Error(`message HTTP ${res.status}`);
      const data = (await res.json()) as CopilotResponse;
      setResponse(data);
      setMessage('');
    } catch (err) {
      setError(`Copilot request failed: ${(err as Error).message}`);
    } finally {
      setLoading(false);
    }
  };

  const actor = (() => {
    try {
      return getCurrentUser().displayName;
    } catch {
      return 'Anonymous';
    }
  })();

  if (defence.active) {
    return (
      <div data-testid="copilot-dock-defence" style={{ padding: tokens.space[2], minHeight: 0 }}>
        <Callout severity="warning" title="Copilot is disabled" icon="smart_toy">
          <Text variant="body" as="p" style={{ margin: 0 }}>
            Defence / local-only mode is active. No external model or copilot service traffic is started from the browser
            in this build.
            {defence.reason ? ` (${defence.reason})` : ''}
          </Text>
        </Callout>
      </div>
    );
  }

  return (
    <div data-testid="copilot-dock" style={{ padding: tokens.space[2], minHeight: 0, maxHeight: '100%' }}>
      <Stack direction="column" gap={2} style={{ minWidth: 0 }}>
        <Stack direction="row" align="center" justify="space-between" gap={1} style={{ flexWrap: 'wrap' }}>
          <Text variant="subtitle" as="h2" id="gvw-copilot-title" style={{ margin: 0 }}>
            Copilot
          </Text>
          <Text variant="caption" as="span" style={{ color: tokens.color.ink.secondary }}>
            {actor}
          </Text>
        </Stack>

        <Card
          level={1}
          header="Registered tools"
          data-testid="copilot-tool-list"
        >
          {tools.length === 0 && !error ? (
            <Text variant="caption" as="p" style={{ margin: 0 }}>
              No tools yet.
            </Text>
          ) : null}
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {tools.map((tool) => (
              <li
                key={tool.id}
                style={{
                  borderBottom: tokens.border.thin,
                  padding: `${tokens.space[1]}px 0`,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: tokens.space[1],
                }}
              >
                <code style={{ fontFamily: tokens.font.family.mono, fontSize: tokens.font.size.sm }}>{tool.id}</code>
                <span style={{ color: tokens.color.ink.secondary, fontSize: tokens.font.size.sm }}>{tool.costClass}</span>
                {tool.defenceVariantSafe ? <Icon name="shield_lock" size={16} decorative /> : null}
              </li>
            ))}
          </ul>
        </Card>

        <form onSubmit={onSubmit} aria-labelledby="gvw-copilot-title">
          <Field label="Prompt">
            {({ id, 'aria-describedby': ad }) => (
            <textarea
              id={id}
              name="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Ask about catalog, layers, or map actions"
              aria-label="Copilot message"
              aria-describedby={ad}
              style={{
                width: '100%',
                minHeight: 96,
                minWidth: 0,
                boxSizing: 'border-box',
                borderRadius: tokens.radius.input,
                border: tokens.border.thin,
                background: tokens.color.surface[0],
                color: tokens.color.ink.primary,
                fontSize: tokens.font.size.md,
                fontFamily: tokens.font.family.ui,
                padding: tokens.space[2],
                resize: 'vertical',
              }}
            />
            )}
          </Field>
          <div style={{ marginTop: tokens.space[2] }}>
            <Button
              type="submit"
              disabled={loading}
              style={{ minHeight: tokens.density.touchMin, display: 'inline-flex', alignItems: 'center', gap: tokens.space[1] }}
            >
              {loading ? (
                <>
                  <Spinner size="sm" aria-label="Loading" />
                  <span>Sending</span>
                </>
              ) : (
                'Send'
              )}
            </Button>
          </div>
        </form>

        {error ? (
          <Text
            role="status"
            variant="body"
            as="p"
            style={{ margin: 0, color: tokens.color.semantic.danger }}
          >
            {error}
          </Text>
        ) : null}

        {response ? (
          <Card
            level={1}
            header="Last response"
            data-testid="copilot-last-response"
          >
            <Text variant="body" as="p" style={{ margin: 0 }}>
              {response.assistantMessage}
            </Text>
            <Text variant="caption" as="p" style={{ margin: `${tokens.space[2]}px 0 0` }}>
              decision: <strong>{response.decision}</strong>
              {response.toolId ? ` · tool: ${response.toolId}` : ''}
              {response.auditLogged ? ' · audit logged' : ' · audit pending'}
            </Text>
            <ul
              style={{
                margin: `${tokens.space[2]}px 0 0`,
                paddingInlineStart: tokens.space[3],
                listStyle: 'disc',
                fontSize: tokens.font.size.sm,
              }}
            >
              {response.toolTrace.map((step) => (
                <li key={`${step.guardrail}-${step.toolId ?? 'none'}-${step.reason ?? 'ok'}`}>
                  <code
                    style={{
                      fontFamily: tokens.font.family.mono,
                      color: tokens.color.ink.primary,
                    }}
                  >
                    {step.guardrail}
                  </code>
                  {` `}
                  {step.decision}
                  {step.reason ? ` (${step.reason})` : ''}
                </li>
              ))}
            </ul>
          </Card>
        ) : null}
      </Stack>
    </div>
  );
}
